import { apiClient } from './apiClient'
import { setSessionToken } from '../auth/session'

export const ACTIVE_ORG_LS_KEY = 'veilstream_active_organization_id'
export const ACTIVE_ORG_CHANGED_EVENT = 'veilstream:active-organization-changed'

/** Dispatched when the active organization changes; org-scoped contexts should refetch. */
export function notifyActiveOrganizationChanged(): void {
  window.dispatchEvent(new Event(ACTIVE_ORG_CHANGED_EVENT))
}

export type UserOrganization = {
  organization_id: number
  name: string
  slug: string
  role: string
  is_default: boolean
}

export function getActiveOrganizationId(): number | null {
  const raw = localStorage.getItem(ACTIVE_ORG_LS_KEY)
  if (!raw?.trim()) return null
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : null
}

export function setActiveOrganizationId(id: number): void {
  const prev = getActiveOrganizationId()
  localStorage.setItem(ACTIVE_ORG_LS_KEY, String(id))
  if (prev !== id) {
    notifyActiveOrganizationChanged()
  }
}

export function clearActiveOrganizationId(): void {
  if (getActiveOrganizationId() != null) {
    localStorage.removeItem(ACTIVE_ORG_LS_KEY)
    notifyActiveOrganizationChanged()
    return
  }
  localStorage.removeItem(ACTIVE_ORG_LS_KEY)
}

/** Keeps localStorage active org in sync with orgs the user belongs to. */
export function reconcileActiveOrganization(
  organizations: UserOrganization[],
  preferredOrganizationId?: number,
): number | null {
  const validIds = new Set(organizations.map((o) => o.organization_id))
  const stored = getActiveOrganizationId()

  if (organizations.length > 0) {
    if (stored && validIds.has(stored)) {
      return stored
    }
    if (preferredOrganizationId && validIds.has(preferredOrganizationId)) {
      setActiveOrganizationId(preferredOrganizationId)
      return preferredOrganizationId
    }
    const defaultOrg = organizations.find((o) => o.is_default) ?? organizations[0]
    if (defaultOrg) {
      setActiveOrganizationId(defaultOrg.organization_id)
      return defaultOrg.organization_id
    }
    clearActiveOrganizationId()
    return null
  }

  if (preferredOrganizationId) {
    setActiveOrganizationId(preferredOrganizationId)
    return preferredOrganizationId
  }
  if (stored) {
    return stored
  }
  clearActiveOrganizationId()
  return null
}

export async function fetchUserOrganizations(): Promise<{
  organizations: UserOrganization[]
  active_organization_id: number
}> {
  return apiClient.get('/api/user/organizations')
}

export async function switchActiveOrganization(organizationId: number): Promise<{
  message: string
  active_organization_id: number
  role: string
  token?: string
}> {
  const result = await apiClient.post('/api/user/active-organization', {
    organization_id: organizationId,
  })
  if (result.token) {
    setSessionToken(result.token)
  }
  setActiveOrganizationId(result.active_organization_id)
  return result
}

/** Reconcile localStorage with memberships; re-issue JWT if active org differs from token. */
export async function syncActiveOrganizationWithJWT(
  jwtOrganizationId: number | undefined,
  organizations: UserOrganization[],
  preferredOrganizationId?: number,
): Promise<number | null> {
  const activeId = reconcileActiveOrganization(
    organizations,
    preferredOrganizationId ?? jwtOrganizationId,
  )
  if (activeId && jwtOrganizationId && activeId !== jwtOrganizationId) {
    try {
      const result = await switchActiveOrganization(activeId)
      return result.active_organization_id
    } catch {
      if (jwtOrganizationId) {
        setActiveOrganizationId(jwtOrganizationId)
      }
      return jwtOrganizationId ?? null
    }
  }
  return activeId
}
